import React from 'react';
import { Alert, Button, DatePicker, Descriptions, Form, Input, Modal, Spin, Typography } from 'antd';
import { DOCUMENT_UI_DATETIME_FORMAT } from '../../../../utils/documentUiFormat';
import dayjs from 'dayjs';
import { useGetClosingPackageProjectionQuery } from '../../api/closingPackageApi';
import { useGetApplicationByIdQuery } from '../../../../store/api/applicationsApi';
import { closingPackageWorkflowStateMeta } from '../../lib/workflowLabels';

const { Text } = Typography;

export type ClosingPackageModalShellProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  applicationId: number;
  taskType: string;
  /** Placeholder copy for main form area (IMP-001H-2). */
  placeholderMessage: string;
  executeLabel?: string;
};

/**
 * Shared ERP modal shell for Closing Package workspace tasks (IMP-001H-1).
 * Read-only context + placeholder execute — no domain mutations.
 */
const ClosingPackageModalShell: React.FC<ClosingPackageModalShellProps> = ({
  open,
  onClose,
  title,
  applicationId,
  taskType,
  placeholderMessage,
  executeLabel = 'Выполнить',
}) => {
  const [form] = Form.useForm();

  const { data: application, isLoading: applicationLoading } = useGetApplicationByIdQuery(applicationId, {
    skip: !open || !applicationId,
  });

  const { data: projection, isLoading: projectionLoading } = useGetClosingPackageProjectionQuery(
    applicationId,
    { skip: !open || !applicationId }
  );

  React.useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }
    form.setFieldsValue({
      eventAt: dayjs(),
      comment: '',
    });
  }, [form, open]);

  const workflowMeta = projection
    ? closingPackageWorkflowStateMeta(projection.workflowState)
    : null;

  const contextLoading = applicationLoading || projectionLoading;

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      destroyOnClose
      width={640}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>Отмена</Button>
          <Button type="primary" disabled title="Будет доступно на этапе IMP-001H-2">
            {executeLabel} (Not implemented)
          </Button>
        </div>
      }
    >
      {contextLoading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : (
        <>
          <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Заявка">
              {application
                ? `#${application.id} — ${application.internalNumber || application.externalNumber || '—'}`
                : `#${applicationId}`}
            </Descriptions.Item>
            <Descriptions.Item label="Тип задачи">{taskType}</Descriptions.Item>
            <Descriptions.Item label="Комплект — статус">
              {workflowMeta ? workflowMeta.label : projection ? projection.workflowState : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Режим состава">
              {projection?.compositionMode ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Заблокирован">
              {projection ? (projection.locked ? 'Да' : 'Нет') : '—'}
            </Descriptions.Item>
          </Descriptions>

          <Alert
            type="info"
            showIcon
            message="Основное содержимое"
            description={placeholderMessage}
            style={{ marginBottom: 16 }}
          />

          <Form form={form} layout="vertical">
            <Form.Item name="eventAt" label="Дата события">
              <DatePicker
                showTime
                style={{ width: '100%' }}
                disabled
                format={DOCUMENT_UI_DATETIME_FORMAT}
              />
            </Form.Item>
            <Form.Item name="comment" label="Комментарий">
              <Input.TextArea rows={3} placeholder="Комментарий (IMP-001H-2)" disabled />
            </Form.Item>
          </Form>

          <Text type="secondary" style={{ fontSize: 12 }}>
            Просмотр состава — Application → Документы → Комплект закрывающих документов.
          </Text>
        </>
      )}
    </Modal>
  );
};

export default ClosingPackageModalShell;
